import React, { useEffect, useMemo, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ChatMessage as ChatMessageType, BalanceSuggestions } from '../types';
import './Message.css';

const GREETING_QUICK_ACTIONS = [
  "How's my business?",
  'Show P&L summary',
  'Can you Export all my active customers?',
  'How to create an invoice?',
];

export interface MessageProps {
  message: ChatMessageType;
  onQuickActionClick?: (question: string) => void;
}

const TABLE_FALLBACK_TEXT = 'You can see the table in chat.';

function sanitizeForSpeech(rawText: string): string[] {
  if (!rawText?.trim()) return [];

  let text = rawText.replace(/\r\n/g, '\n');
  const lines = text.split('\n');
  const outputLines: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const current = lines[i];
    const next = lines[i + 1] ?? '';
    const isTableRow = /^\s*\|.*\|\s*$/.test(current);
    const isTableSeparator = /^\s*\|?[\s:-]+\|[\s|:-]*$/.test(next);

    if (isTableRow && isTableSeparator) {
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
        i += 1;
      }
      outputLines.push(TABLE_FALLBACK_TEXT);
      continue;
    }

    outputLines.push(current);
    i += 1;
  }

  text = outputLines.join('\n');
  text = text.replace(/₹\s*([\d,]+(?:\.\d+)?)/g, (_match, amount: string) => {
    const normalizedAmount = amount.replace(/,/g, '');
    return `${normalizedAmount} rupees`;
  });
  text = text.replace(/<table[\s\S]*?<\/table>/gi, ` ${TABLE_FALLBACK_TEXT} `);
  text = text.replace(/```[\s\S]*?```/g, ' ');
  text = text.replace(/`([^`]+)`/g, '$1');
  text = text.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '$1');
  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1');
  text = text.replace(/^#{1,6}\s+/gm, '');
  text = text.replace(/^\s{0,3}>\s?/gm, '');
  text = text.replace(/^\s*[-*+]\s+/gm, '');
  text = text.replace(/^\s*\d+\.\s+/gm, '');
  text = text.replace(/(\*\*|__|\*|_|~~)/g, '');
  text = text.replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '');
  text = text.replace(/\s*[-–—]{2,}\s*/g, ' ');
  text = text.replace(/[|]+/g, ' ');
  text = text.replace(/\s+/g, ' ').trim();

  if (!text) return [];

  const sentenceChunks = text
    .split(/(?<=[.!?])\s+/)
    .map((chunk) => chunk.trim())
    .filter(Boolean);

  const chunks: string[] = [];
  let currentChunk = '';
  sentenceChunks.forEach((sentence) => {
    if ((currentChunk + ' ' + sentence).trim().length > 220) {
      if (currentChunk) chunks.push(currentChunk.trim());
      currentChunk = sentence;
    } else {
      currentChunk = `${currentChunk} ${sentence}`.trim();
    }
  });
  if (currentChunk) chunks.push(currentChunk);
  return chunks.length > 0 ? chunks : [text];
}

function BalanceSuggestionChips({ suggestions, onSelect }: { suggestions: BalanceSuggestions; onSelect: (question: string) => void }) {
  const ledgers = suggestions.ledgers ?? [];
  const groups = suggestions.groups ?? [];
  if (ledgers.length === 0 && groups.length === 0) return null;

  return (
    <div className="balance-suggestion-chips">
      <p className="balance-suggestion-hint">Click a suggestion below to get that balance.</p>
      {ledgers.length > 0 && (
        <div className="balance-suggestion-group">
          <span className="balance-suggestion-label">Ledgers:</span>
          <div className="balance-suggestion-list">
            {ledgers.map((item) => (
              <button
                key={item.account_name}
                type="button"
                className="balance-suggestion-chip"
                onClick={() => onSelect(`Give me the balance of the ledger "${item.account_name}" for the current financial year.`)}
              >
                {item.account_name}
              </button>
            ))}
          </div>
        </div>
      )}
      {groups.length > 0 && (
        <div className="balance-suggestion-group">
          <span className="balance-suggestion-label">Groups:</span>
          <div className="balance-suggestion-list">
            {groups.map((item) => (
              <button
                key={item.group_name}
                type="button"
                className="balance-suggestion-chip"
                onClick={() => onSelect(`Give me the balance of the group "${item.group_name}" for the current financial year.`)}
              >
                {item.group_name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Message({ message, onQuickActionClick }: MessageProps) {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  const isGreeting = message.role === 'assistant' && message.message_type === 'greeting';
  const isAssistant = message.role === 'assistant';
  const [isSpeaking, setIsSpeaking] = useState(false);
  const activeUtterancesRef = useRef<SpeechSynthesisUtterance[]>([]);
  const speechChunks = useMemo(() => sanitizeForSpeech(message.content), [message.content]);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const stopSpeech = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
    activeUtterancesRef.current = [];
  };

  const speakChunks = (chunks: string[], index = 0) => {
    if (index >= chunks.length) {
      setIsSpeaking(false);
      activeUtterancesRef.current = [];
      return;
    }

    const utterance = new SpeechSynthesisUtterance(chunks[index]);
    utterance.rate = 1;
    utterance.pitch = 1;
    utterance.onend = () => speakChunks(chunks, index + 1);
    utterance.onerror = () => {
      setIsSpeaking(false);
      activeUtterancesRef.current = [];
    };
    activeUtterancesRef.current.push(utterance);
    window.speechSynthesis.speak(utterance);
  };

  const handleSpeakToggle = () => {
    if (!('speechSynthesis' in window)) return;
    if (isSpeaking) {
      stopSpeech();
      return;
    }
    if (speechChunks.length === 0) return;
    console.log('[ChatBot][TTS] Sanitized speech output:', speechChunks.join(' '));
    window.speechSynthesis.cancel();
    setIsSpeaking(true);
    speakChunks(speechChunks);
  };

  useEffect(() => {
    return () => {
      if (isSpeaking) {
        window.speechSynthesis.cancel();
      }
    };
  }, [isSpeaking]);

  if (isSystem) {
    return (
      <div className="message system">
        <div className="message-bubble system">
          <div className="message-text">{message.content}</div>
        </div>
      </div>
    );
  }

  const contentNode = isUser ? message.content : <Markdown remarkPlugins={[remarkGfm]}>{message.content}</Markdown>;

  return (
    <div className={`message ${isUser ? 'user' : 'assistant'}`}>
      <div className="message-bubble">
        <div className="message-text">{contentNode}</div>
        {isGreeting && onQuickActionClick && (
          <div className="message-quick-actions">
            {GREETING_QUICK_ACTIONS.map((label) => (
              <button key={label} type="button" className="quick-action-btn" onClick={() => onQuickActionClick(label)}>
                {label}
              </button>
            ))}
          </div>
        )}
        {!isUser && message.balance_suggestions && onQuickActionClick && (
          <BalanceSuggestionChips suggestions={message.balance_suggestions} onSelect={onQuickActionClick} />
        )}
        <div className="message-meta">
          {isAssistant && (
            <button
              type="button"
              className={`message-speak-btn ${isSpeaking ? 'message-speak-btn-active' : ''}`}
              onClick={handleSpeakToggle}
              title={isSpeaking ? 'Stop reading' : 'Read this message'}
              aria-label={isSpeaking ? 'Stop reading this message' : 'Read this message aloud'}
            >
              {isSpeaking ? (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="8" height="8" rx="1.2" fill="currentColor" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M14.5 5.5a1 1 0 0 1 1.64-.77l3.7 2.98a3 3 0 0 1 0 4.58l-3.7 2.98A1 1 0 0 1 14.5 14.5V5.5ZM5 9h3.5l4-3v12l-4-3H5a2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2Z"
                    fill="currentColor"
                  />
                </svg>
              )}
            </button>
          )}
          <div className="message-timestamp">{formatTime(message.timestamp)}</div>
        </div>
      </div>
    </div>
  );
}
